import { Placement } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import type { CardCarouselProps } from '@iheartjane/menu';
import { CardCarousel, MagicRowSubtitle } from '@iheartjane/menu';
import { Typography } from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import { pluralize } from '@iheartjane/utils';
import { useMemo } from 'react';

import { LinkWithBasename } from '@jane/bloom/components/linkWithBasename';
import { ProductCard } from '@jane/bloom/components/productCard/productCard';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { Cart } from '@jane/bloom/types/schemas/bloom/checkout';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { BrandSpecials } from '@jane/bloom/types/schemas/marketplace/brandSpecials';
import type {
  ProductApplicableIds,
  StoreSpecials,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { createCustomRowWeightFilter } from '@jane/bloom/utils/specials/shouldShowWeight';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import { buildSingleProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearchResult';
import type { StoreViewModel } from '@jane/bloom/viewModels/store';

import styles from './menuRow.module.css';

type MenuRowProps = {
  brandSpecials: BrandSpecials;
  cart?: Nullable<Cart>;
  customLanguage: CustomLanguage;
  customRowId: number;
  customRowSlug: string;
  displayName: Nullable<string>;
  janeGold: JaneGold;
  mode?: CardCarouselProps['mode'];
  pinnedStore?: Nullable<StoreViewModel>;
  placement?: Placement;
  specials: Nullable<StoreSpecials>;
  stringifiedInstance: string;
};

export function MenuRow({
  brandSpecials,
  cart,
  customLanguage,
  customRowId,
  customRowSlug,
  displayName,
  janeGold,
  mode = 'ideal-width',
  pinnedStore,
  placement,
  specials,
  stringifiedInstance,
}: MenuRowProps) {
  const { featureFlags } = useRootLoaderData();
  const thematicRowContextFlag = featureFlags[FLAGS.c3ThematicRowsContext];
  const instance = useMemoizedRowInstance(stringifiedInstance);

  const weightFilter = useMemo(() => {
    const rules = pinnedStore?.custom_rows?.find(
      (cr) => cr.id === customRowId
    )?.rules;
    return createCustomRowWeightFilter(rules);
  }, [pinnedStore?.custom_rows, customRowId]);

  if (!instance || instance.products.length === 0) {
    return null;
  }

  const isNonCustomizableRow =
    placement === Placement.MenuDynamicRow ||
    placement === Placement.BuyAgainRow;

  const shouldShowViewAll =
    instance.products.length > 10 && !isNonCustomizableRow;

  const viewAllCustomRowLink = `/rows/${customRowId}/${customRowSlug}`;

  const totalProducts = isNonCustomizableRow
    ? instance.products.length
    : instance.totalProducts;

  return (
    <CardCarousel
      className="customRow"
      cardWidth={240}
      cardWidthMobile={162}
      cardGap={24}
      cardGapMobile={16}
      mode={mode}
      name={displayName ?? ''}
      beforeHeader={
        <div className={styles['menuRowLabel']}>
          {isNonCustomizableRow ? (
            <Typography
              color="text-main"
              as="h2"
              variant="header-bold"
              maxLines={2}
              overflowWrap="anywhere"
            >
              {displayName}
            </Typography>
          ) : (
            <LinkWithBasename to={viewAllCustomRowLink}>
              <Typography
                color="text-main"
                as="h2"
                variant="header-bold"
                maxLines={2}
                overflowWrap="anywhere"
              >
                {displayName}
              </Typography>
            </LinkWithBasename>
          )}
          {thematicRowContextFlag && placement === Placement.MenuDynamicRow ? (
            <MagicRowSubtitle textColor="grays-mid" iconColor="primary" />
          ) : (
            <Typography color="grays-mid" variant="body">
              {pluralize({ noun: 'product', number: totalProducts })}
            </Typography>
          )}
        </div>
      }
      afterHeader={
        shouldShowViewAll ? (
          <LinkWithBasename
            aria-label={`View all ${displayName} products`}
            className={styles['viewAll']}
            to={viewAllCustomRowLink}
            variant="reefer-underline"
          >
            View all
          </LinkWithBasename>
        ) : undefined
      }
    >
      {instance.products.map((product) => {
        const filteredPriceIds = weightFilter
          ? (product.attributes.available_weights?.filter((weight) =>
              weightFilter(weight, product)
            ) as ProductApplicableIds[])
          : undefined;

        return (
          <CardCarousel.Card key={product.objectId}>
            <ProductCard
              product={buildSingleProductSearchResultViewModel({
                applicablePriceIds: filteredPriceIds,
                bundleTag: featureFlags[FLAGS.c1BundleTag],
                cart,
                customLanguage,
                janeGold,
                pinnedStore,
                product: product.attributes,
                specials,
              })}
              brandSpecials={brandSpecials}
              smartProductInstance={product}
              customRowId={customRowId}
              isSponsored={product.isSponsored}
              selected={() => product.click()}
              weightFilter={weightFilter}
            />
          </CardCarousel.Card>
        );
      })}
    </CardCarousel>
  );
}
